import React, { useState, useEffect, useRef } from 'react';
import Layout from '@/components/Layout';
import { useAuth } from '@/contexts/AuthContext';
import axios from '@/utils/axiosConfig';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Users, AlertCircle, MessageSquare, FileText, TrendingUp, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const ClientsList = () => {
  const { user, token } = useAuth();
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [selectedClient, setSelectedClient] = useState(null);
  const [commentDialogOpen, setCommentDialogOpen] = useState(false);
  const [commentContent, setCommentContent] = useState('');
  const [commentPriority, setCommentPriority] = useState('moyenne');

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const API = `${BACKEND_URL}/api`;

  // Protection contre les mises à jour sur composant démonté
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchClients();
    }
  }, []);

  useEffect(() => {
    filterClients();
  }, [clients, searchTerm, priorityFilter]);

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/clients-list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (isMountedRef.current) {
        setClients(response.data);
      }
    } catch (error) {
      if (isMountedRef.current) {
        toast.error('Erreur lors du chargement des clients');
      }
      console.error('Error fetching clients:', error);
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  };

  const filterClients = () => {
    let filtered = [...clients];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(client =>
        client.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        client.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (client.file_number && client.file_number.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    // Priority filter
    if (priorityFilter !== 'all') {
      filtered = filtered.filter(client => client.priority === priorityFilter);
    }

    setFilteredClients(filtered);
  };

  const updatePriority = async (clientId, priority) => {
    try {
      await axios.put(
        `${API}/users/${clientId}/priority?priority=${priority}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Priorité mise à jour');
      fetchClients();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour de la priorité');
    }
  };

  const submitComment = async () => {
    if (!selectedClient || !commentContent.trim()) {
      toast.error('Veuillez remplir le commentaire');
      return;
    }

    try {
      await axios.post(
        `${API}/client-comments`,
        {
          client_id: selectedClient.id,
          content: commentContent,
          priority: commentPriority
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Commentaire envoyé à l\'employé assigné');
      setCommentContent('');
      setCommentPriority('moyenne');
      setCommentDialogOpen(false);
      fetchClients();
    } catch (error) {
      toast.error('Erreur lors de l\'envoi du commentaire');
    }
  };

  const downloadClientCredentials = async (clientId, clientName) => {
    try {
      toast.info('Génération des identifiants en cours...');
      
      const response = await axios.post(
        `${API}/users/${clientId}/download-credentials`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Identifiants_${clientName.replace(/\s+/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('📄 Identifiants générés ! Un nouveau mot de passe temporaire a été créé.');
      toast.warning('⚠️ Le client devra changer son mot de passe à la première connexion.', { duration: 5000 });
    } catch (error) {
      toast.error('Erreur lors de la génération des identifiants');
      console.error('Error downloading credentials:', error);
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent':
        return 'bg-red-600 text-white';
      case 'haute':
        return 'bg-orange-500 text-white';
      case 'moyenne':
        return 'bg-blue-500 text-white';
      case 'basse':
        return 'bg-gray-400 text-white';
      default:
        return 'bg-gray-300 text-gray-700';
    }
  };

  const getPriorityLabel = (priority) => {
    switch (priority) {
      case 'urgent':
        return '🔴 Urgent';
      case 'haute':
        return '🟠 Haute';
      case 'moyenne':
        return '🔵 Moyenne';
      case 'basse':
        return '⚪ Basse';
      default:
        return 'Non définie';
    }
  };

  if (user?.role !== 'admin') {
    return (
      <Layout>
        <div className="text-center py-12">
          <AlertCircle className="h-16 w-16 mx-auto mb-4 text-red-600" />
          <p className="text-xl text-gray-600">Accès réservé aux administrateurs</p>
        </div>
      </Layout>
    );
  }

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-red-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900">
              Clients Actifs
            </h1>
            <p className="text-gray-600 mt-2">
              Suivez l'état d'avancement, priorités et commentaires de vos clients actifs
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Badge className="bg-gradient-to-r from-red-600 to-blue-600 text-white px-4 py-2">
              <Users className="h-4 w-4 mr-2" />
              {clients.length} client(s)
            </Badge>
          </div>
        </div>

        {/* Filters */}
        <Card className="border-0 shadow-xl">
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Recherche</Label>
                <Input
                  placeholder="Nom, email, numéro de dossier..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="mt-2"
                />
              </div>
              <div>
                <Label>Filtrer par priorité</Label>
                <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                  <SelectTrigger className="mt-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Toutes les priorités</SelectItem>
                    <SelectItem value="urgent">🔴 Urgent</SelectItem>
                    <SelectItem value="haute">🟠 Haute</SelectItem>
                    <SelectItem value="moyenne">🔵 Moyenne</SelectItem>
                    <SelectItem value="basse">⚪ Basse</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Clients List */}
        <div className="grid grid-cols-1 gap-4">
          {filteredClients.map((client) => (
            <Card key={client.id} className="border-0 shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <h3 className="text-xl font-bold text-gray-900">{client.full_name}</h3>
                      <Badge className={getPriorityColor(client.priority)}>
                        {getPriorityLabel(client.priority)}
                      </Badge>
                      {client.file_status && (
                        <Badge variant="outline">{client.file_status}</Badge>
                      )}
                      {client.group_name && (
                        <Badge className="bg-purple-100 text-purple-800 flex items-center gap-1">
                          <Users className="h-3 w-3" />
                          {client.group_name}
                        </Badge>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600">
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4" />
                        <span>Dossier: {client.file_number || 'Non créé'}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4" />
                        <span>Créé le: {format(new Date(client.created_at), 'dd/MM/yyyy')}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <MessageSquare className="h-4 w-4" />
                        <span>{client.comment_count} commentaire(s)</span>
                      </div>
                    </div>

                    {/* Progress Bar - État d'avancement */}
                    {client.progress !== undefined && (
                      <div className="mt-4">
                        <div className="flex items-center justify-between text-sm mb-2">
                          <span className="font-semibold text-gray-700 flex items-center gap-2">
                            <TrendingUp className="h-4 w-4 text-green-600" />
                            État d'avancement
                          </span>
                          <span className="font-bold text-blue-600">{client.progress || 0}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-3">
                          <div
                            className="bg-gradient-to-r from-green-500 to-blue-600 h-3 rounded-full transition-all duration-500"
                            style={{ width: `${client.progress || 0}%` }}
                          ></div>
                        </div>
                        {client.file_status && (
                          <p className="text-xs text-gray-500 mt-1">
                            Statut actuel: {client.file_status}
                          </p>
                        )}
                      </div>
                    )}

                    <div className="mt-3 text-sm">
                      <p className="text-gray-600">Email: {client.email}</p>
                      {client.phone && <p className="text-gray-600">Téléphone: {client.phone}</p>}
                      {client.assigned_employees && client.assigned_employees.length > 0 && (
                        <p className="text-gray-600">Employé assigné: {client.assigned_employees.length} employé(s)</p>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col gap-2 ml-4">
                    <Select
                      value={client.priority || 'moyenne'}
                      onValueChange={(value) => updatePriority(client.id, value)}
                    >
                      <SelectTrigger className="w-40">
                        <SelectValue placeholder="Priorité" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="urgent">🔴 Urgent</SelectItem>
                        <SelectItem value="haute">🟠 Haute</SelectItem>
                        <SelectItem value="moyenne">🔵 Moyenne</SelectItem>
                        <SelectItem value="basse">⚪ Basse</SelectItem>
                      </SelectContent>
                    </Select>

                    <Button
                      onClick={() => {
                        setSelectedClient(client);
                        setCommentDialogOpen(true);
                      }}
                      variant="outline"
                      size="sm"
                      className="flex items-center gap-2"
                    >
                      <MessageSquare className="h-4 w-4" />
                      Commenter
                    </Button>

                    <Button
                      onClick={() => downloadClientCredentials(client.id, client.full_name)}
                      className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
                      size="sm"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                      Identifiants
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}

          {filteredClients.length === 0 && (
            <Card className="border-0 shadow-lg">
              <CardContent className="p-12 text-center text-gray-500">
                <Users className="h-16 w-16 mx-auto mb-4 opacity-30" />
                <p>Aucun client trouvé</p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Comment Dialog */}
        <Dialog open={commentDialogOpen} onOpenChange={setCommentDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>
                Ajouter un commentaire pour {selectedClient?.full_name}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Priorité du commentaire</Label>
                <Select value={commentPriority} onValueChange={setCommentPriority}>
                  <SelectTrigger className="mt-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="urgent">🔴 Urgent</SelectItem>
                    <SelectItem value="haute">🟠 Haute</SelectItem>
                    <SelectItem value="moyenne">🔵 Moyenne</SelectItem>
                    <SelectItem value="basse">⚪ Basse</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Commentaire</Label>
                <Textarea
                  value={commentContent}
                  onChange={(e) => setCommentContent(e.target.value)}
                  placeholder="Votre commentaire sera automatiquement envoyé à l'employé assigné..."
                  rows={6}
                  className="mt-2"
                />
                <p className="text-xs text-gray-500 mt-2">
                  {selectedClient?.assigned_employees && selectedClient.assigned_employees.length > 0
                    ? '✓ Ce commentaire sera envoyé par message à l\'employé assigné'
                    : '⚠️ Aucun employé assigné - le commentaire sera enregistré mais non envoyé'}
                </p>
              </div>

              <Button
                onClick={submitComment}
                className="w-full bg-gradient-to-r from-red-600 to-blue-600"
              >
                <MessageSquare className="mr-2 h-4 w-4" />
                Envoyer le commentaire
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default ClientsList;
